import React, { useState, useEffect } from "react";
import CustomerTable from "../features/customer/CustomerTable/CustomerTable";
import MspTable from "../features/msp/MspTable";
import { fetchUserMspId } from "../utils/authUtils";
import { useNavigate } from "react-router-dom";
import { logout } from "../services/authService";

interface DashboardAdminProps {
  role: "superadmin" | "msp";
  mspId?: number;
}

export default function DashboardAdmin({
  role,
  mspId,
}: Readonly<DashboardAdminProps>) {
  const navigate = useNavigate();
  const isSuperAdmin = role === "superadmin";

  const [selectedMspId, setSelectedMspId] = useState<number | null>(null);
  const [userMspId, setUserMspId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const initializeMspId = async () => {
      try {
        setLoading(true);
        setError(null);

        if (role === "msp") {
          if (mspId !== undefined && mspId !== null && mspId > 0) {
            setUserMspId(mspId);
          } else {
            const fetchedMspId = await fetchUserMspId();

            if (
              fetchedMspId === null ||
              fetchedMspId === 0 ||
              Number.isNaN(fetchedMspId)
            ) {
              console.error("No MSP ID found – redirecting to login Page");
              await logout();
              return;
            }

            setUserMspId(fetchedMspId);
          }
        } else if (role === "superadmin") {
          if (mspId !== undefined && mspId !== null && mspId > 0) {
            setSelectedMspId(mspId);
          } else {
            setSelectedMspId(null);
          }
        }
      } catch (err: any) {
        console.error("Error initializing MSP ID:", err);

        const message = typeof err?.message === "string" ? err.message : "";
        if (
          err?.name === "UserUnauthenticatedException" ||
          message.includes("needs to be authenticated") ||
          message.includes("No access token available")
        ) {
          console.warn("User unauthenticated – redirecting to login Page");
          await logout();
          return;
        }

        setError("Failed to load user information");
      } finally {
        setLoading(false);
      }
    };

    initializeMspId();
  }, [role, mspId]);

  const handleBack = () => {
    setSelectedMspId(null);
    navigate("/msps");
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="p-8 text-center">
          <div className="text-gray-600">Loading...</div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-8 text-center">
          <h2 className="text-lg font-semibold text-red-600">Error</h2>
          <p className="text-gray-600 mt-2">{error}</p>
        </div>
      );
    }

    // Superadmin flow
    if (isSuperAdmin) {
      if (selectedMspId !== null) {
        return (
          <CustomerTable mspId={selectedMspId.toString()} onBack={handleBack} />
        );
      }
      return <MspTable />;
    }

    // MSP user flow
    if (userMspId === null || userMspId === 0) {
      return (
        <div className="p-8 text-center">
          <p className="text-gray-600 mt-2">Redirecting to login Page...</p>
        </div>
      );
    }
    return <CustomerTable mspId={userMspId.toString()} />;
  };

  return <div className="p-6">{renderContent()}</div>;
}
